<template>
    <div class="relative min-h-screen bg-slate-50 overflow-hidden">

        <!-- CHARGEMENT -->
        <div v-if="loadingGetData" class="flex flex-col items-center justify-center min-h-screen gap-4 text-gray-400">
            <i class="fas fa-spinner fa-spin text-4xl text-blue-500"></i>
            <p class="text-sm">Chargement…</p>
        </div>

        <!-- ERREUR -->
        <div v-else-if="!chantier" class="flex items-center justify-center min-h-screen px-6">
            <div class="text-center">
                <i class="fas fa-exclamation-triangle text-5xl text-amber-400 mb-4"></i>
                <h2 class="text-lg font-semibold text-gray-700">Impossible de charger le chantier</h2>
                <p class="text-sm text-gray-500 mt-1">Vérifiez votre connexion et réessayez.</p>
            </div>
        </div>

        <!-- FLOW PRINCIPAL -->
        <template v-else>

            <!-- Barre de progression fixe en haut -->
            <div class="fixed top-0 left-0 right-0 z-50 h-1.5 bg-gray-200">
                <div
                    class="h-full bg-blue-600 transition-all duration-500 ease-out"
                    :style="{ width: progressBarWidth + '%' }"
                ></div>
            </div>

            <!-- Indicateur étape coin haut droit -->
            <div class="fixed top-3 right-4 z-50 bg-white text-xs font-semibold text-gray-500 px-3 py-1.5 rounded-full shadow-sm border border-gray-100">
                <span class="text-blue-600">{{ currentStep + 1 }}</span>
                <span class="mx-0.5 text-gray-300">/</span>
                <span>{{ totalSteps }}</span>
            </div>

            <!-- Contenu avec slide transition -->
            <transition
                :enter-class="slideEnterClass"
                enter-active-class="transition-transform duration-300 ease-out"
                :leave-active-class="'transition-transform duration-300 ease-out absolute inset-0 overflow-hidden'"
                :leave-to-class="slideLeaveToClass"
            >
                <div :key="currentStep" class="min-h-screen px-5 pt-14 pb-16 overflow-y-auto">
                    <div class="max-w-sm mx-auto w-full">

                        <!-- ETAPE 0 : RÉCAPITULATIF -->
                        <template v-if="currentStep === 0">

                            <div class="text-center mb-8 mt-6">
                                <img
                                    v-if="chantier.societe.logo"
                                    :src="chantier.societe.logo"
                                    class="h-16 object-contain mx-auto mb-4 rounded-xl"
                                />
                                <h1 class="text-2xl font-bold text-gray-900">{{ chantier.societe.nom }}</h1>
                                <p class="text-sm text-gray-500 mt-1">
                                    {{ chantier.societe.adresse }}, {{ chantier.societe.code_postal }} {{ chantier.societe.ville }}
                                </p>
                            </div>

                            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-3">
                                <p class="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 flex items-center gap-1.5">
                                    <i class="fas fa-user"></i> Client
                                </p>
                                <p class="font-semibold text-gray-900 text-base">
                                    {{ chantier.client.prenom }} {{ chantier.client.nom }}
                                </p>
                                <p class="text-sm text-gray-500 mt-0.5">{{ chantier.client.telephone }}</p>
                            </div>

                            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
                                <p class="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 flex items-center gap-1.5">
                                    <i class="fas fa-map-marker-alt"></i> Lieu d'intervention
                                </p>
                                <p class="font-semibold text-gray-900 text-base">{{ chantier.adresse.adresse1 }}</p>
                                <p class="text-sm text-gray-500 mt-0.5">
                                    {{ chantier.adresse.code_postal }} {{ chantier.adresse.ville }}
                                </p>
                                <p v-if="chantier.adresse.acces" class="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3 flex items-center gap-1.5">
                                    <i class="fas fa-key"></i> Code d'accès : {{ chantier.adresse.acces }}
                                </p>
                                <p v-if="chantier.adresse.notes" class="text-xs italic text-gray-400 mt-2">{{ chantier.adresse.notes }}</p>
                            </div>

                            <div class="flex flex-wrap gap-2 mb-8">
                                <span class="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full border border-blue-100">
                                    <i class="fas fa-wrench"></i>
                                    {{ chantier.interventions.length }} intervention{{ chantier.interventions.length > 1 ? 's' : '' }}
                                </span>
                                <span class="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 text-xs font-medium px-3 py-1.5 rounded-full border border-violet-100">
                                    <i class="fas fa-file-alt"></i>
                                    {{ chantier.documents.length }} document{{ chantier.documents.length > 1 ? 's' : '' }}
                                </span>
                                <span v-if="chantier.terminer" class="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs font-medium px-3 py-1.5 rounded-full border border-green-100">
                                    <i class="fas fa-check-circle"></i> Terminé
                                </span>
                                <span v-else class="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-100">
                                    <i class="fas fa-clock"></i> En cours
                                </span>
                            </div>

                            <button
                                @click="goNext"
                                class="w-full py-4 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-base rounded-2xl flex items-center justify-center gap-2.5 transition-all shadow-lg shadow-blue-200"
                            >
                                <i class="fas fa-file-alt"></i>
                                Voir les documents
                                <i class="fas fa-arrow-right text-sm opacity-80"></i>
                            </button>

                        </template>

                        <!-- ETAPES DOCUMENTS -->
                        <template v-else-if="currentStep >= 1 && currentStep <= chantier.documents.length">

                            <p class="text-xs font-bold uppercase tracking-wider text-gray-400 mt-6 mb-6">
                                Document {{ currentStep }} sur {{ chantier.documents.length }}
                            </p>

                            <div class="text-center mb-8">
                                <div class="w-20 h-20 rounded-3xl bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-5">
                                    <i :class="[docIcon(currentDoc.type), 'text-blue-600 text-3xl']"></i>
                                </div>
                                <h2 class="text-2xl font-bold text-gray-900">
                                    {{ docTypeLabels[currentDoc.type] || currentDoc.type }}
                                </h2>
                                <p v-if="currentDoc.intervention_id" class="text-sm text-gray-500 mt-1.5">
                                    <i class="fas fa-tools mr-1 text-gray-400"></i>
                                    Intervention #{{ getInterventionIndex(currentDoc.intervention_id) + 1 }}
                                    &middot; {{ getInterventionRef(currentDoc.intervention_id) }}
                                </p>
                                <p v-else class="text-sm text-gray-500 mt-1.5">
                                    <i class="fas fa-folder mr-1 text-gray-400"></i>
                                    Document de chantier
                                </p>
                            </div>

                            <a
                                :href="currentDoc.url"
                                target="_blank"
                                class="flex items-center justify-center gap-2 py-3.5 border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 text-gray-600 rounded-2xl text-sm font-semibold transition-all mb-7"
                            >
                                <i class="fas fa-eye"></i>
                                Consulter le document
                                <i class="fas fa-external-link-alt text-xs opacity-50"></i>
                            </a>

                            <!-- Premier document : zone de signature -->
                            <template v-if="currentStep === 1">
                                <div class="bg-blue-50 border border-blue-100 rounded-2xl px-4 pt-4 pb-3 mb-5">
                                    <p class="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-1.5">
                                        <i class="fas fa-pen-nib"></i>
                                        Votre signature
                                    </p>
                                    <p class="text-xs text-blue-600 mb-3">
                                        Signez dans le cadre ci-dessous. Elle s'appliquera à l'ensemble des documents.
                                    </p>
                                    <input-signature
                                        v-model="signature"
                                        :keyName="'chantier-' + $route.params.token"
                                        btn-label="Enregistrer ma signature"
                                        btn-class="w-full mt-3 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-base transition-colors cursor-pointer"
                                        @input="onSignatureSaved"
                                    />
                                </div>
                                <p class="text-xs text-center text-gray-400 flex items-center justify-center gap-1.5">
                                    <i class="fas fa-shield-alt"></i>
                                    Cette signature électronique a valeur contractuelle
                                </p>
                            </template>

                            <!-- Documents intermédiaires -->
                            <template v-else-if="currentStep < chantier.documents.length">
                                <button
                                    @click="goNext"
                                    class="w-full py-4 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-base rounded-2xl flex items-center justify-center gap-2.5 transition-all shadow-lg shadow-blue-200"
                                >
                                    Document suivant
                                    <i class="fas fa-arrow-right text-sm opacity-80"></i>
                                </button>
                            </template>

                            <!-- Dernier document : validation -->
                            <template v-else>
                                <div class="bg-green-50 border border-green-200 rounded-2xl p-4 mb-5">
                                    <p class="text-sm text-green-800 leading-relaxed flex items-start gap-2">
                                        <i class="fas fa-info-circle mt-0.5 flex-shrink-0"></i>
                                        En validant, vous confirmez avoir pris connaissance de tous les documents présentés.
                                    </p>
                                </div>
                                <button
                                    @click="submitSignatures"
                                    class="w-full py-4 bg-green-600 hover:bg-green-700 active:scale-95 text-white font-bold text-base rounded-2xl flex items-center justify-center gap-2.5 transition-all shadow-lg shadow-green-200"
                                >
                                    <i class="fas fa-check-circle"></i>
                                    Valider et signer
                                </button>
                            </template>

                        </template>

                        <!-- ETAPE FINALE : CONFIRMATION -->
                        <template v-else>
                            <div class="flex flex-col items-center text-center pt-20">
                                <div class="w-24 h-24 rounded-full bg-green-100 border-4 border-green-200 flex items-center justify-center mb-6">
                                    <i class="fas fa-check text-green-500 text-4xl"></i>
                                </div>
                                <h2 class="text-3xl font-bold text-gray-900 mb-3">Merci !</h2>
                                <p class="text-base text-gray-500 leading-relaxed mb-10">
                                    Tout est signé et enregistré.<br>
                                    Vous allez recevoir vos documents par e-mail.
                                </p>
                                <a
                                    v-if="chantier.societe.has_portail"
                                    :href="'/c/' + chantier.client.token"
                                    target="_blank"
                                    class="inline-flex items-center gap-2.5 px-7 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl transition-colors shadow-lg shadow-blue-200"
                                >
                                    <i class="fas fa-desktop"></i>
                                    Mon espace client
                                </a>
                            </div>
                        </template>

                    </div>
                </div>
            </transition>

        </template>

        <!-- MODALE SIGNATURE EN COURS -->
        <transition
            enter-class="opacity-0"
            enter-active-class="transition-opacity duration-200"
            leave-active-class="transition-opacity duration-200"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showSignModal"
                class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
                style="background: rgba(0,0,0,0.55);"
            >
                <div class="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">

                    <!-- En cours -->
                    <template v-if="!signatureComplete">

                        <div class="px-6 pt-6 pb-4 border-b border-gray-100">
                            <h3 class="text-base font-bold text-gray-900">Signature en cours…</h3>
                            <p class="text-sm text-gray-400 mt-0.5">Ne fermez pas cette page</p>
                        </div>

                        <div class="px-5 py-4 space-y-2 max-h-64 overflow-y-auto">
                            <div
                                v-for="item in signQueue"
                                :key="item.doc.id"
                                class="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
                                :class="{
                                    'bg-blue-50':  item.status === 'signing',
                                    'bg-green-50': item.status === 'done',
                                    'bg-gray-50':  item.status === 'pending',
                                }"
                            >
                                <div
                                    class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs"
                                    :class="{
                                        'bg-blue-100 text-blue-500':   item.status === 'signing',
                                        'bg-green-100 text-green-500': item.status === 'done',
                                        'bg-gray-100 text-gray-400':   item.status === 'pending',
                                    }"
                                >
                                    <i v-if="item.status === 'pending'" class="fas fa-clock"></i>
                                    <i v-else-if="item.status === 'signing'" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-check"></i>
                                </div>
                                <span class="flex-1 text-sm font-medium text-gray-700 truncate">
                                    {{ docTypeLabels[item.doc.type] || item.doc.type }}
                                </span>
                                <span v-if="item.status === 'signing'" class="text-xs text-blue-500 font-semibold whitespace-nowrap">En cours…</span>
                                <span v-else-if="item.status === 'done'" class="text-xs text-green-600 font-semibold whitespace-nowrap">Signé</span>
                            </div>

                            <div v-if="loadingSendMail" class="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-amber-50">
                                <div class="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 text-xs">
                                    <i class="fas fa-envelope"></i>
                                </div>
                                <span class="flex-1 text-sm font-medium text-gray-700">Envoi par e-mail</span>
                                <span class="text-xs text-amber-600 font-semibold">En cours…</span>
                            </div>
                        </div>

                        <div class="px-6 pb-6 pt-2 flex items-center gap-3">
                            <div class="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    class="h-full bg-blue-500 rounded-full transition-all duration-500"
                                    :style="{ width: progressPercent + '%' }"
                                ></div>
                            </div>
                            <span class="text-xs text-gray-400 font-semibold whitespace-nowrap">
                                {{ signedCount }}/{{ signQueue.length }}
                            </span>
                        </div>

                    </template>

                    <!-- Terminé -->
                    <template v-else>
                        <div class="px-6 py-12 flex flex-col items-center text-center">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                                <i class="fas fa-check-circle text-green-500 text-3xl"></i>
                            </div>
                            <h3 class="text-xl font-bold text-gray-900 mb-2">Tout est signé !</h3>
                            <p class="text-sm text-gray-500 mb-7 leading-relaxed">
                                Vos documents sont enregistrés et vous seront envoyés par e-mail.
                            </p>
                            <button
                                @click="closeSignModal"
                                class="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    </template>

                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import DocType from './../../config/docType.js';

export default {
    name: 'SignChantier',

    data() {
        return {
            chantier: null,
            docTypeLabels: DocType,
            typeLabels: { 507: 'Entretien', 512: 'Dépannage' },
            signature: null,
            loadingGetData: false,
            currentStep: 0,
            goingForward: true,
            showSignModal: false,
            signatureComplete: false,
            signQueue: [],
            loadingSendMail: false,
        };
    },

    computed: {
        totalSteps() {
            if (!this.chantier) return 1;
            return 1 + this.chantier.documents.length + 1;
        },

        progressBarWidth() {
            if (this.totalSteps <= 1) return 100;
            return Math.round((this.currentStep / (this.totalSteps - 1)) * 100);
        },

        currentDoc() {
            if (!this.chantier || this.currentStep < 1 || this.currentStep > this.chantier.documents.length) return null;
            return this.chantier.documents[this.currentStep - 1];
        },

        isAlreadySigned() {
            if (!this.chantier) return false;
            return this.chantier.interventions.some(i => i.signature_client !== null);
        },

        signedCount() {
            return this.signQueue.filter(i => i.status === 'done').length;
        },

        progressPercent() {
            if (!this.signQueue.length) return 0;
            return Math.round((this.signedCount / this.signQueue.length) * 100);
        },

        slideEnterClass() {
            return this.goingForward ? 'translate-x-full' : '-translate-x-full';
        },

        slideLeaveToClass() {
            return this.goingForward ? '-translate-x-full' : 'translate-x-full';
        },
    },

    methods: {
        async fetchChantierData() {
            this.loadingGetData = true;
            try {
                const response = await this.$request.post('get-intervention-token', {
                    intervention_token: this.$route.params.token,
                });
                this.chantier = response;

                const logoUrl = this.chantier.societe?.logo;
                if (logoUrl) {
                    const oldLink = document.querySelector("link[rel~='icon']");
                    if (oldLink) oldLink.remove();
                    const link = document.createElement('link');
                    link.rel = 'icon'; link.type = 'image/png'; link.href = logoUrl;
                    document.head.appendChild(link);
                }
                document.title = `Signature — ${this.chantier.societe?.nom || 'Chantier'}`;

                if (this.isAlreadySigned) {
                    this.currentStep = this.totalSteps - 1;
                }
            } catch (e) {
                this.$toast.error('Impossible de charger le chantier.');
            } finally {
                this.loadingGetData = false;
            }
        },

        goNext() {
            this.goingForward = true;
            this.currentStep = Math.min(this.currentStep + 1, this.totalSteps - 1);
        },

        /**
         * Appelé par @input du composant input-signature
         * (se déclenche quand l'utilisateur clique sur le bouton interne "Enregistrer")
         */
        onSignatureSaved() {
            if (!this.signature) return;
            // Un seul document → on valide directement
            if (this.chantier.documents.length === 1) {
                this.submitSignatures();
            } else {
                // Plusieurs docs → on passe au document suivant
                this.goNext();
            }
        },

        getInterventionIndex(id) {
            return this.chantier.interventions.findIndex(i => i.id === id);
        },

        getInterventionRef(id) {
            const i = this.chantier.interventions.find(i => i.id === id);
            return i ? i.reference : '';
        },

        docIcon(type) {
            const icons = {
                bon_intervention:      'fas fa-clipboard-check',
                contrat_entretien:     'fas fa-file-contract',
                attestation_entretien: 'fas fa-certificate',
                rapport_chantier:      'fas fa-hard-hat',
                facture:               'fas fa-file-invoice-dollar',
                devis:                 'fas fa-file-signature',
            };
            return icons[type] || 'fas fa-file-alt';
        },

        async submitSignatures() {
            if (!this.signature) {
                return this.$toast.error('Aucune signature enregistrée.');
            }
            this.signQueue = this.chantier.documents.map(doc => ({ doc, status: 'pending' }));
            this.signatureComplete = false;
            this.showSignModal = true;
            await this.$nextTick();
            await this.runSignQueue();
        },

        async runSignQueue() {
            for (let i = 0; i < this.signQueue.length; i++) {
                this.$set(this.signQueue, i, { ...this.signQueue[i], status: 'signing' });
                try {
                    await this.$request.post(`/doc-intervention/${this.signQueue[i].doc.id}/sign`, {
                        url_signature: this.signature,
                    });
                } catch (e) {
                    console.error(`Erreur signature doc #${this.signQueue[i].doc.id}`, e);
                }
                this.$set(this.signQueue, i, { ...this.signQueue[i], status: 'done' });
            }

            this.loadingSendMail = true;
            await this.$request.post('/chantier/send-documents', { chantier_id: this.chantier.id });
            this.loadingSendMail = false;

            await this.sleep(500);
            this.signatureComplete = true;
            this.fetchChantierData();
        },

        closeSignModal() {
            this.showSignModal = false;
            this.signatureComplete = false;
            this.signQueue = [];
            this.goingForward = true;
            this.currentStep = this.totalSteps - 1;
        },

        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
    },

    mounted() {
        this.fetchChantierData();
    },
};
</script>